import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Button, Input } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  MoveLeft,
  Target,
  Smile,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Lightbulb,
  ClipboardList,
  Heart,
  ArrowLeft,
  ArrowRight,
  MessageSquare,
  BarChart2,
  AlertTriangle,
  Newspaper,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import "./SocialListingAgent.scss";
import {
  fetchSocialListeningAnalysisDetails,
  SocialListeningAnalysisDetails,
  SocialListeningHighlight,
  SocialListeningSourceBreakdown,
  SocialListeningSourcePost,
} from "src/services/SocialListeningAPIs";
import html2canvas from "html2canvas";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import jsPDF from "jspdf";
import ReportMarkdown from "./ReportMarkdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faReddit } from "@fortawesome/free-brands-svg-icons";
import { getDemoAnalysisDetails, isDemoAnalysisId } from "./demoSocialListeningReports";
import { formatNumber } from "src/utils/numberFormat";

const POSTS_PAGE_SIZE = 2;

const formatPct = (value: number, decimals = 1, language = "en") =>
  `${formatNumber(Number(value.toFixed(decimals)), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }, language)}%`;

const getPostLikes = (post: SocialListeningSourcePost) => {
  if (typeof post.likes_count === "number") {return post.likes_count;}
  if (typeof post.post_likes === "number") {return post.post_likes;}
  if (typeof post.engagement?.likes === "number") {return post.engagement.likes;}
  return null;
};

const getSourceIcon = (sourceName: string) => {
  const key = (sourceName || "").toLowerCase();
  if (key.includes("instagram")) {return "/Icons/Insta-logo.webp";}
  if (key.includes("facebook")) {return "/Icons/facebook-logo.png";}
  if (key.includes("reddit")) {return "/Icons/social-listening.svg";}
  if (key.includes("tiktok")) {return "/Icons/social-listening.svg";}
  return "/Icons/social-listening.svg";
};

const getHighlightText = (highlight: SocialListeningHighlight): string => {
  if (typeof highlight === "string") {return highlight;}
  return highlight.text;
};

const isRedditSource = (sourceName: string) => sourceName.toLowerCase().includes("reddit");

const toTitleCase = (str: string) => str.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const formatLlmKey = (key: string) => {
  if (key.startsWith("comparison_")) {
    return `Comparison: ${toTitleCase(key.slice("comparison_".length))}`;
  }
  return toTitleCase(key);
};

const sortLlmEntries = (entries: [string, string | string[]][]) => {
  const executive = entries.filter(([key]) => key === "executive_summary");
  const comparisons = entries.filter(([key]) => key !== "executive_summary" && key.startsWith("comparison_"));
  const rest = entries.filter(([key]) => key !== "executive_summary" && !key.startsWith("comparison_"));
  return [...executive, ...comparisons, ...rest];
};

const LLM_KEY_ICONS: Record<string, LucideIcon> = {
  executive_summary: FileText,
  risks_and_opportunities: Lightbulb,
  strategic_recommendations: ClipboardList,
  comparison_posts: Newspaper,
  comparison_comments: MessageSquare,
  comparison_metrics: BarChart2,
  risks: AlertTriangle,
  opportunities: Lightbulb,
  recommendations: ClipboardList,
};

const getLlmKeyIcon = (key: string): LucideIcon =>
  LLM_KEY_ICONS[key] ?? FileText;

interface SourcePostsPanelProps {
  source: SocialListeningSourceBreakdown;
  sourceIndex: number;
}

const SourcePostsPanel: React.FC<SourcePostsPanelProps> = ({ source, sourceIndex }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const PreviousIcon = isArabic ? ChevronRight : ChevronLeft;
  const NextIcon = isArabic ? ChevronLeft : ChevronRight;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const posts = source.posts ?? [];

  const filteredPosts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {return posts;}
    return posts.filter(post => {
      const details = (post.post_details || "").toLowerCase();
      const type = (post.type || "").toLowerCase();
      const comments = (post.recent_comments || []).join(" ").toLowerCase();
      return details.includes(query) || type.includes(query) || comments.includes(query);
    });
  }, [posts, search]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * POSTS_PAGE_SIZE;
  const pagePosts = filteredPosts.slice(pageStart, pageStart + POSTS_PAGE_SIZE);
  const rangeStart = filteredPosts.length ? pageStart + 1 : 0;
  const rangeEnd = Math.min(pageStart + POSTS_PAGE_SIZE, filteredPosts.length);

  useEffect(() => {
    setPage(1);
  }, [search, sourceIndex]);

  const sourceLabel = source.source_name || t("socialListingAgent.reportPage.unknownSource");

  return (
    <div className="source-posts-panel">
      <div className="source-posts-panel-header">
        <h4>
          {t("socialListingAgent.reportPage.sourcePostsTitle", { source: sourceLabel })}
        </h4>
        <Input
          className="source-posts-search"
          placeholder={t("socialListingAgent.reportPage.searchPostsPlaceholder")}
          prefix={<img src="/Icons/search.svg" alt="" className="source-posts-search-icon" />}
          value={search}
          onChange={event => setSearch(event.target.value)}
          allowClear
          aria-label={t("socialListingAgent.reportPage.searchPostsPlaceholder")}
        />
      </div>

      {posts.length === 0 ? (
        <p className="source-posts-empty">{t("socialListingAgent.reportPage.noPosts")}</p>
      ) : (
        <>
          <div className="source-posts-table-wrap">
            <table className="source-posts-table">
              <thead>
                <tr>
                  <th>{t("socialListingAgent.reportPage.postDetails")}</th>
                  <th>{t("socialListingAgent.reportPage.postType")}</th>
                  <th>{t("socialListingAgent.reportPage.recentComments")}</th>
                  <th>{t("socialListingAgent.reportPage.engagement")}</th>
                </tr>
              </thead>
              <tbody>
                {pagePosts.map((post: SocialListeningSourcePost, index) => (
                  <tr key={post.post_id || `${sourceIndex}-post-${pageStart + index}`}>
                    <td className="source-post-details">
                      {post.post_url ? (
                        <a href={post.post_url} target="_blank" rel="noopener noreferrer">
                          {post.post_details}
                        </a>
                      ) : (
                        post.post_details
                      )}
                    </td>
                    <td>
                      <span className="source-post-type">{post.type || "-"}</span>
                    </td>
                    <td className="source-post-comments">
                      <div className="source-post-comments-inner">
                        {(post.recent_comments || []).length
                          ? (post.recent_comments || []).map((comment, commentIndex) => (
                              <span key={`comment-${commentIndex}`} className="source-post-comment">
                                {comment}
                              </span>
                            ))
                          : "-"}
                      </div>
                    </td>
                    <td className="source-post-engagement">
                      <div className="source-post-engagement-inner">
                        <span className="source-post-likes-value">
                          <Heart size={15} aria-hidden />
                          <span>
                            {(() => {
                              const likes = getPostLikes(post);
                              return likes === null ? "-" : formatNumber(likes, {}, i18n.language);
                            })()}
                          </span>
                        </span>
                        <span className="source-post-likes-label">
                          {t("socialListingAgent.reportPage.likes")}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="source-posts-pagination">
            <span className="source-posts-pagination-summary">
              {filteredPosts.length ? (
                <>
                  {t("socialListingAgent.reportPage.postsPaginationShowing")}{" "}
                  <strong>
                    {formatNumber(rangeStart, {}, i18n.language)}-{formatNumber(rangeEnd, {}, i18n.language)}
                  </strong>{" "}
                  {t("socialListingAgent.reportPage.postsPaginationOf")}{" "}
                  <strong>{formatNumber(filteredPosts.length, {}, i18n.language)}</strong>{" "}
                  {t("socialListingAgent.reportPage.postsPaginationEntries")}
                </>
              ) : null}
            </span>
            <div className="source-posts-pagination-actions">
              <button
                type="button"
                className="source-posts-page-btn"
                disabled={safePage <= 1}
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                aria-label={t("socialListingAgent.reportPage.previous")}
              >
                <PreviousIcon size={16} strokeWidth={2.4} aria-hidden />
              </button>
              <span className="source-posts-page-indicator" aria-current="page">
                {formatNumber(safePage, {}, i18n.language)}
              </span>
              <button
                type="button"
                className="source-posts-page-btn"
                disabled={safePage >= totalPages}
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                aria-label={t("socialListingAgent.reportPage.next")}
              >
                <NextIcon size={16} strokeWidth={2.4} aria-hidden />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const CompetitorAnalysisReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const HighlightArrowIcon = isArabic ? ArrowLeft : ArrowRight;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const [analysis, setAnalysis] = useState<SocialListeningAnalysisDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedSourceIndex, setExpandedSourceIndex] = useState<number | null>(null);
  const reportRef = useRef<HTMLDivElement | null>(null);
  const hasAutoDownloaded = useRef(false);
  const hasAutoExpandedSource = useRef(false);

  const analysisId = Number(searchParams.get("analysis_id") || params.analysisId || "NaN");
  const isValidAnalysisId =
    Number.isFinite(analysisId) && (analysisId > 0 || isDemoAnalysisId(analysisId));

  useEffect(() => {
    if (!isValidAnalysisId) {
      return;
    }

    if (isDemoAnalysisId(analysisId)) {
      setAnalysis(getDemoAnalysisDetails(analysisId, i18n.language));
      return;
    }

    const userIdRaw = localStorage.getItem("user_id");
    const userId = Number(userIdRaw);
    if (userIdRaw === null || Number.isNaN(userId)) {
      return;
    }

    setIsLoading(true);
    fetchSocialListeningAnalysisDetails(userId, analysisId)
      .then(data => setAnalysis(data))
      .catch(error => {
        console.error("Social listening details error:", error);
      })
      .finally(() => setIsLoading(false));
  }, [analysisId, isValidAnalysisId, i18n.language]);

  useEffect(() => {
    if (!analysis?.source_breakdown?.length || hasAutoExpandedSource.current) {
      return;
    }
    hasAutoExpandedSource.current = true;
    setExpandedSourceIndex(0);
  }, [analysis?.source_breakdown]);

  const downloadPdf = async () => {
    if (!reportRef.current) {return;}
    const canvas = await html2canvas(reportRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let position = 0;
    let heightLeft = imgHeight;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const name = analysis?.company_name
      ? `${analysis.company_name}-report.pdf`
      : "social-listening-report.pdf";
    pdf.save(name);
  };

  useEffect(() => {
    if (!analysis || !reportRef.current || !isValidAnalysisId) {return;}
    if (searchParams.get("download") !== "1") {return;}
    if (hasAutoDownloaded.current) {return;}

    hasAutoDownloaded.current = true;
    downloadPdf();
  }, [analysis, searchParams, isValidAnalysisId]);

  const sentiment = analysis?.overall_sentiment;
  const positivePct = sentiment?.positive_percentage ?? 0;
  const neutralPct = sentiment?.neutral_percentage ?? 0;
  const negativePct = sentiment?.negative_percentage ?? 0;
  const totalPosts = sentiment?.total_posts ?? 0;
  const totalTexts = sentiment?.total_texts ?? 0;

  const executiveSummary =
    analysis?.executive_summary?.trim() || analysis?.narrative_summary?.trim() || "";

  const highlights = analysis?.highlights?.length
    ? analysis.highlights
    : analysis?.key_insights ?? [];

  const reportTitle =
    analysis?.analysis_type === "brand"
      ? t("socialListingAgent.reportPage.brandTitle")
      : t("socialListingAgent.reportPage.title");

  const toggleSource = (index: number) => {
    setExpandedSourceIndex(prev => (prev === index ? null : index));
  };

  return (
    <div className="report-container">
      <div className="report-header">
        <Button className="history-back-button" onClick={() => navigate(-1)}>
          <MoveLeft className="Brand-back-button" />
          {t("socialListingAgent.reportPage.back")}
        </Button>
        <h1 className="report-title">{reportTitle}</h1>
      </div>

      <Card className="report-card" bordered={false} ref={reportRef}>
        <div className="report-card-header">
          <h2>{analysis?.company_name || t("socialListingAgent.reportPage.fallbackReportTitle")}</h2>
          <div className="report-actions">
            <Tippy content={t("socialListingAgent.reportPage.downloadTooltip")} placement="top">
              <button className="report-icon-button" type="button" onClick={downloadPdf}>
                <img src="/Icons/Downloads.svg" className="report-icon" alt="" />
              </button>
            </Tippy>
          </div>
        </div>

        <div className="report-section">
          <div className="report-section-title sentiment-title">
            <Smile className="report-title-icon" />
            <span>{t("socialListingAgent.reportPage.overallSentiment")}</span>
          </div>
          <div className="sentiment-stats">
            <div className="sentiment-stat sentiment-total">
              <div className="sentiment-value">
                {sentiment ? formatNumber(totalPosts, {}, i18n.language) : "-"}
              </div>
              <div className="sentiment-label">{t("socialListingAgent.reportPage.totalPosts")}</div>
              {totalTexts > 0 && totalTexts !== totalPosts ? (
                <div className="sentiment-sublabel">
                  {t("socialListingAgent.reportPage.totalTextsAnalyzed", {
                    count: totalTexts,
                    countFormatted: formatNumber(totalTexts, {}, i18n.language),
                  })}
                </div>
              ) : null}
            </div>
            <div className="sentiment-stat sentiment-positive">
              <div className="sentiment-value">
                {sentiment ? formatNumber(sentiment.positive_count, {}, i18n.language) : "-"}
                <span className="sentiment-inline-pct">
                  ({formatPct(positivePct, positivePct % 1 === 0 ? 1 : 2, i18n.language)})
                </span>
              </div>
              <div className="sentiment-label">{t("socialListingAgent.reportPage.positive")}</div>
            </div>
            <div className="sentiment-stat sentiment-neutral">
              <div className="sentiment-value">
                {sentiment ? formatNumber(sentiment.neutral_count, {}, i18n.language) : "-"}
                <span className="sentiment-inline-pct">
                  ({formatPct(neutralPct, neutralPct % 1 === 0 ? 2 : 1, i18n.language)})
                </span>
              </div>
              <div className="sentiment-label">{t("socialListingAgent.reportPage.neutral")}</div>
            </div>
            <div className="sentiment-stat sentiment-negative">
              <div className="sentiment-value">
                {sentiment ? formatNumber(sentiment.negative_count, {}, i18n.language) : "-"}
                <span className="sentiment-inline-pct">
                  ({formatPct(negativePct, negativePct % 1 === 0 ? 1 : 2, i18n.language)})
                </span>
              </div>
              <div className="sentiment-label">{t("socialListingAgent.reportPage.negative")}</div>
            </div>
          </div>
          <div className="sentiment-bar">
            <span className="sentiment-segment positive" style={{ width: `${positivePct}%` }} />
            <span className="sentiment-segment neutral" style={{ width: `${neutralPct}%` }} />
            <span className="sentiment-segment negative" style={{ width: `${negativePct}%` }} />
          </div>
        </div>

        <div className="report-section report-section-sources">
          <div className="report-section-title">
            <Target className="report-title-icon" aria-hidden />
            <span>{t("socialListingAgent.reportPage.sourceBreakdown")}</span>
          </div>
          <div className="source-list">
            {(analysis?.source_breakdown || []).length === 0 && !isLoading ? (
              <p className="source-list-empty">{t("socialListingAgent.reportPage.noSources")}</p>
            ) : (
              (analysis?.source_breakdown || []).map((source, index) => {
                const isExpanded = expandedSourceIndex === index;
                return (
                  <div
                    key={`${source?.source_name ?? "source"}-${index}`}
                    className={`source-accordion${isExpanded ? " is-expanded" : ""}`}
                  >
                    <button
                      type="button"
                      className="source-row"
                      onClick={() => toggleSource(index)}
                      aria-expanded={isExpanded}
                    >
                      <div className="source-platform">
                        {isRedditSource(source?.source_name || "") ? (
                          <span className="source-reddit-icon" aria-hidden>
                            <FontAwesomeIcon icon={faReddit} />
                          </span>
                        ) : (
                          <img
                            src={getSourceIcon(source?.source_name || "")}
                            alt={
                              source?.source_name ||
                              t("socialListingAgent.reportPage.sourceAltFallback")
                            }
                          />
                        )}
                        <span>
                          {source?.source_name ||
                            t("socialListingAgent.reportPage.unknownSource")}
                        </span>
                      </div>
                      <div className="source-metrics">
                        <span>
                          {t("socialListingAgent.reportPage.commentsCount", {
                            count: source?.total_comments ?? 0,
                            countFormatted: formatNumber(source?.total_comments ?? 0, {}, i18n.language),
                          })}
                        </span>
                        <span className="positive">
                          {formatPct(source?.sentiment_data?.positive_percentage ?? 0, 1, i18n.language)}{" "}
                          {t("socialListingAgent.reportPage.positive").toLowerCase()}
                        </span>
                        <span className="negative">
                          {formatPct(source?.sentiment_data?.negative_percentage ?? 0, 1, i18n.language)}{" "}
                          {t("socialListingAgent.reportPage.negative").toLowerCase()}
                        </span>
                        <ChevronDown
                          className={`source-chevron${isExpanded ? " is-open" : ""}`}
                          size={18}
                          aria-hidden
                        />
                      </div>
                    </button>
                    {isExpanded ? (
                      <SourcePostsPanel source={source} sourceIndex={index} />
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {analysis?.llm_comparison && Object.keys(analysis.llm_comparison).length > 0 ? (
          sortLlmEntries(Object.entries(analysis.llm_comparison)).map(([key, value]) => {
            const Icon = getLlmKeyIcon(key);
            const title = formatLlmKey(key);
            const isArray = Array.isArray(value);
            const hasContent = isArray
              ? (value as string[]).length > 0
              : typeof value === "string" && value.trim().length > 0;

            return (
              <div key={key} className="report-section report-section-summary">
                <div className="report-section-title">
                  <Icon className="report-title-icon" aria-hidden />
                  <span>{title}</span>
                </div>
                <div className="report-summary-box">
                  {hasContent ? (
                    isArray ? (
                      <ul className="report-actions-list">
                        {(value as string[]).map((item, index) => (
                          <li key={`${key}-${index}`}>
                            <ReportMarkdown content={item} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ReportMarkdown content={value as string} />
                    )
                  ) : (
                    <p>
                      {isLoading
                        ? t("socialListingAgent.reportPage.loadingSummary")
                        : `No ${title.toLowerCase()} available.`}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <>
            <div className="report-section report-section-summary">
              <div className="report-section-title">
                <FileText className="report-title-icon" />
                <span>{t("socialListingAgent.reportPage.executiveSummary")}</span>
              </div>
              <div className="report-summary-box">
                {executiveSummary ? (
                  <ReportMarkdown content={executiveSummary} />
                ) : (
                  <p>
                    {isLoading
                      ? t("socialListingAgent.reportPage.loadingSummary")
                      : t("socialListingAgent.reportPage.noSummary")}
                  </p>
                )}
              </div>
            </div>

            <div className="report-section report-section-highlights">
              <div className="report-section-title">
                <Lightbulb className="report-title-icon" />
                <span>{t("socialListingAgent.reportPage.highlights")}</span>
              </div>
              <div className="report-summary-box">
                {highlights.length ? (
                  <ul className="report-highlights-list">
                    {highlights.map((highlight, index) => (
                      <li key={`highlight-${index}`} className="report-highlight-item">
                        <span className="report-highlight-arrow" aria-hidden>
                          <HighlightArrowIcon size={16} strokeWidth={2.6} />
                        </span>
                        <div className="report-highlight-content">
                          <ReportMarkdown content={getHighlightText(highlight)} />
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>
                    {isLoading
                      ? t("socialListingAgent.reportPage.loadingHighlights")
                      : t("socialListingAgent.reportPage.noHighlights")}
                  </p>
                )}
              </div>
            </div>

            <div className="report-section report-section-actions">
              <div className="report-section-title">
                <ClipboardList className="report-title-icon" />
                <span>{t("socialListingAgent.reportPage.recommendedActions")}</span>
              </div>
              <div className="report-summary-box">
                {analysis?.recommended_actions?.length ? (
                  <ul className="report-actions-list">
                    {analysis.recommended_actions.map((action, index) => (
                      <li key={`action-${index}`}>
                        <ReportMarkdown content={action} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>
                    {isLoading
                      ? t("socialListingAgent.reportPage.loadingActions")
                      : t("socialListingAgent.reportPage.noActions")}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default CompetitorAnalysisReport;
